import React from 'react';
import { Input, InputNumber, Button, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';

interface QueryParamsProps {
  deliveryNoFrom: string;
  deliveryNoTo: string;
  deliverySeqFrom: number;
  deliverySeqTo: number;
  submitNoFrom: string;
  submitNoTo: string;
  testDateFrom: string;
  testDateTo: string;
  reportNoFrom: string;
  reportNoTo: string;
  loading: boolean;
  onDeliveryNoFromChange: (v: string) => void;
  onDeliveryNoToChange: (v: string) => void;
  onDeliverySeqFromChange: (v: number) => void;
  onDeliverySeqToChange: (v: number) => void;
  onSubmitNoFromChange: (v: string) => void;
  onSubmitNoToChange: (v: string) => void;
  onTestDateFromChange: (v: string) => void;
  onTestDateToChange: (v: string) => void;
  onReportNoFromChange: (v: string) => void;
  onReportNoToChange: (v: string) => void;
  onQuery: () => void;
}

const LABEL_WIDTH = 120;

const QueryParams: React.FC<QueryParamsProps> = ({
  deliveryNoFrom, deliveryNoTo,
  deliverySeqFrom, deliverySeqTo,
  submitNoFrom, submitNoTo,
  testDateFrom, testDateTo,
  reportNoFrom, reportNoTo,
  loading,
  onDeliveryNoFromChange, onDeliveryNoToChange,
  onDeliverySeqFromChange, onDeliverySeqToChange,
  onSubmitNoFromChange, onSubmitNoToChange,
  onTestDateFromChange, onTestDateToChange,
  onReportNoFromChange, onReportNoToChange,
  onQuery,
}) => {
  /** BR-FILTER: When "from" loses focus, auto-fill "to" with padded upper bound. */
  const handleDeliveryNoFromBlur = () => {
    const trimmed = deliveryNoFrom.trim();
    if (trimmed && !deliveryNoTo) {
      const padded = (trimmed + 'zzzzzzzzzzzz').substring(0, 8);
      onDeliveryNoToChange(padded);
    }
  };

  const handleSubmitNoFromBlur = () => {
    const trimmed = submitNoFrom.trim();
    if (trimmed && !submitNoTo) {
      const padded = (trimmed + 'zzzzzzzzzzzz').substring(0, 6);
      onSubmitNoToChange(padded);
    }
  };

  const handleReportNoFromBlur = () => {
    const trimmed = reportNoFrom.trim();
    if (trimmed && !reportNoTo) {
      const padded = (trimmed + 'zzzzzzzzzzzz').substring(0, 8);
      onReportNoToChange(padded);
    }
  };

  const parseDateValue = (v: string): Dayjs | null => {
    if (!v) return null;
    const d = dayjs(v);
    return d.isValid() ? d : null;
  };

  const handleDateChange = (setter: (v: string) => void) => (val: Dayjs | null) => {
    setter(val ? val.format('YYYY-MM-DD') : '');
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Delivery number range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_WIDTH }}>繳庫單號 [從]</span>
          <Input
            value={deliveryNoFrom}
            onChange={(e) => onDeliveryNoFromChange(e.target.value)}
            onBlur={handleDeliveryNoFromBlur}
            placeholder="起"
            style={{ width: 140 }}
            maxLength={8}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={deliveryNoTo}
            onChange={(e) => onDeliveryNoToChange(e.target.value)}
            placeholder="迄"
            style={{ width: 140 }}
            maxLength={8}
          />
        </div>
      </div>

      {/* Row 2: Delivery sequence range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_WIDTH }}>繳庫項次 [從]</span>
          <InputNumber
            value={deliverySeqFrom}
            onChange={(v) => onDeliverySeqFromChange(v ?? 0)}
            min={0}
            max={99}
            style={{ width: 140 }}
          />
          <span className="text-sm">[至]</span>
          <InputNumber
            value={deliverySeqTo}
            onChange={(v) => onDeliverySeqToChange(v ?? 99)}
            min={0}
            max={99}
            style={{ width: 140 }}
          />
        </div>
      </div>

      {/* Row 3: Submit number range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_WIDTH }}>送件單號 [從]</span>
          <Input
            value={submitNoFrom}
            onChange={(e) => onSubmitNoFromChange(e.target.value)}
            onBlur={handleSubmitNoFromBlur}
            placeholder="起"
            style={{ width: 140 }}
            maxLength={6}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={submitNoTo}
            onChange={(e) => onSubmitNoToChange(e.target.value)}
            placeholder="迄"
            style={{ width: 140 }}
            maxLength={6}
          />
        </div>
      </div>

      {/* Row 4: Test date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_WIDTH }}>測試完成日 [從]</span>
          <DatePicker
            value={parseDateValue(testDateFrom)}
            onChange={handleDateChange(onTestDateFromChange)}
            placeholder="起始日期"
            style={{ width: 140 }}
          />
          <span className="text-sm">[至]</span>
          <DatePicker
            value={parseDateValue(testDateTo)}
            onChange={handleDateChange(onTestDateToChange)}
            placeholder="結束日期"
            style={{ width: 140 }}
          />
        </div>
      </div>

      {/* Row 5: Report number range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: LABEL_WIDTH }}>報告單號 [從]</span>
          <Input
            value={reportNoFrom}
            onChange={(e) => onReportNoFromChange(e.target.value)}
            onBlur={handleReportNoFromBlur}
            placeholder="起"
            style={{ width: 140 }}
            maxLength={8}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={reportNoTo}
            onChange={(e) => onReportNoToChange(e.target.value)}
            placeholder="迄"
            style={{ width: 140 }}
            maxLength={8}
          />
        </div>
      </div>

      {/* Query button */}
      <div className="flex gap-2 mt-2">
        <Button type="primary" icon={<SearchOutlined />} onClick={onQuery} loading={loading}>
          查詢
        </Button>
      </div>
    </div>
  );
};

export default QueryParams;
